import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import type { components } from '@/api/qraft/schema';
import { AlertBox } from '@/common/AlertBox';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { WBSButton } from '@/common/WBSButton';
import { useMemberTaggingAlert } from '@/groups/coordinator/MemberTaggingAlertContext';
import { StackedAvatars } from '@/tables/StackedAvatars';
import WbsTable, { type Column } from '@/tables/WbsTable';
import { GroupHeaderWrapper } from '~/styles/layoutElements';

type MemberTagItem = components['schemas']['MemberTagItem'];

/**
 * The tag list is unpaginated in legacy (`MemberTagsModel.php:84-87`) and the
 * measured ceiling is 7 tags in one group, so one page is the whole set — but
 * the endpoint's own default is 10, so the limit is passed explicitly rather
 * than left to truncate a group that grows.
 */
const TAG_PAGE_SIZE = 50;

/**
 * The language-independent slug legacy passes to `wbs_contextual_helper`
 * (`member-tagging.php:15`, `member-tagging-edit.php:19`). Without it
 * `TitleWithQuestion` falls back to the *translated* title, so the help target
 * would change with the interface language.
 */
const HELP_KEY = 'study_helper_group_settings';

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/admin/member-tagging/',
)({
  component: MemberTaggingScreen,
});

function MemberTaggingScreen() {
  const { t } = useTranslation();
  const { groupName } = Route.useParams();
  const navigate = useNavigate();
  const { setFlash } = useMemberTaggingAlert();

  const { data: groupData, isPending: groupPending } =
    api.getGroupBySlug.useQuery({
      path: { group_slug: groupName },
    });
  const groupId = groupData?.id ?? 0;

  const { data, isFetching } = api.listGroupMemberTags.useQuery(
    { path: { group_id: groupId }, query: { limit: TAG_PAGE_SIZE } },
    { enabled: groupId > 0 },
  );
  const tags = data?.data ?? [];

  const { mutateAsync: deleteTag, error: deleteError } =
    api.deleteGroupMemberTag.useMutation();

  const handleDelete = async (tagId: number): Promise<void> => {
    // Legacy has no server-side confirmation step either — only this same JS
    // confirm on the delete link (`assets/js/main-script.js:664,2107-2112`,
    // `wbsL10n.group_tag_deletion_confirmation`, which is the string behind
    // `actions.are-you-sure-you-5`).
    if (!window.confirm(t('actions.are-you-sure-you-5'))) return;
    try {
      await deleteTag({ path: { group_id: groupId, tag_id: tagId } });
      await api.listGroupMemberTags.invalidateQueries();
      // The assignments cascade with the tag, so the Tags column on the
      // group's All Students list is stale too.
      await api.getGroupStudents.invalidateQueries();
      setFlash('deleted');
    } catch {
      // Rendered from `deleteError` below.
    }
  };

  const columns: Column<MemberTagItem>[] = [
    {
      key: 'label',
      title: t('wbs.tag-name'),
      sortable: true,
      width: '200px',
    },
    {
      key: 'member_preview',
      title: t('profile.members'),
      // Legacy branches this cell on the live member COUNT, not on the preview
      // it happens to have fetched (`member-tagging.php:42-43`, `:55`, `:59`):
      // the preview is three at most, the count is the whole in-group set.
      render: (_value, row) =>
        row.member_count === 0 ? (
          <NoMembersText>{t('profile.no-members')}</NoMembersText>
        ) : (
          <StackedAvatars
            max={3}
            total={row.member_count}
            members={row.member_preview.map((m) => ({
              id: m.id,
              name: m.display_name,
              avatar: m.avatar || null,
            }))}
          />
        ),
      width: 'auto',
      cellStyle: { verticalAlign: 'middle', padding: '0.5rem 0.3rem' },
    },
    {
      key: 'id',
      title: t('wbs.actions'),
      width: '140px',
      render: (_value, row) => (
        <ActionsCell>
          <ActionLink
            type="button"
            onClick={() =>
              navigate({
                to: '/groups/$groupName/admin/member-tagging/edit/$tagId/',
                params: { groupName, tagId: String(row.id) },
              })
            }
          >
            {t('actions.edit')}
          </ActionLink>
          <span>|</span>
          <ActionLink
            type="button"
            onClick={() => {
              void handleDelete(row.id);
            }}
          >
            {t('actions.delete')}
          </ActionLink>
        </ActionsCell>
      ),
    },
  ];

  return (
    <>
      <GroupHeaderWrapper>
        <TitleWithQuestion
          title={t('profile.member-tagging')}
          helpKey={HELP_KEY}
        />
        <WBSButton
          wide
          onClick={() =>
            navigate({
              to: '/groups/$groupName/admin/member-tagging/add/',
              params: { groupName },
            })
          }
        >
          {t('actions.add-new-tag')}
        </WBSButton>
      </GroupHeaderWrapper>
      {deleteError && (
        <AlertBox variant="error">
          {errorMessage(deleteError, t('wbs.something-went-wrong-please'))}
        </AlertBox>
      )}
      {/* The tag query cannot start until the slug resolves to an id, so the
          group read counts as loading here too — otherwise a group that has
          tags flashes the "no tags" empty state on every first paint. */}
      <WbsTable
        data={tags}
        columns={columns}
        loading={groupPending || isFetching}
        emptyMessage={t('wbs.there-are-no-tags')}
      />
    </>
  );
}

const NoMembersText = styled.span`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 13px;
  font-style: italic;
`;

const ActionsCell = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ActionLink = styled.button`
  background: none;
  border: none;
  color: var(--wbs-red);
  cursor: pointer;
  font-family: var(--raleway);
  font-size: 13px;
  padding: 0;
  text-decoration: none;
`;
